/**
 * EmailHistory Component
 *
 * Displays email communication history for a customer or contact
 * using the backend email history API.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Mail, Paperclip, ChevronDown, ArrowUpRight, ArrowDownLeft, MailX, ExternalLink } from 'lucide-react';
import { emailsApi, type EmailHistoryEntry } from '@/lib/api';
import { Button, Card, CountBadge, EmptyState, Skeleton, StatusPill, cn, formatDatum, formatDatumZeit, formatVor } from '@elbklar/ui';

interface EmailHistoryProps {
  /** Customer ID to fetch email history for */
  customerId?: string | null;
  /** Contact ID to fetch email history for */
  contactId?: string | null;
  /** Maximum number of emails to show initially */
  initialLimit?: number;
}

function EmailSkeleton() {
  return (
    <div className="rounded-lg border border-border p-4">
      <div className="flex items-start gap-3">
        <Skeleton className="size-8 rounded-full" />
        <div className="flex-1">
          <Skeleton className="mb-2 h-5 w-3/4" />
          <Skeleton className="mb-2 h-4 w-1/2" />
          <Skeleton className="h-3 w-1/4" />
        </div>
      </div>
    </div>
  );
}

interface EmailCardProps {
  email: EmailHistoryEntry;
}

function EmailCard({ email }: EmailCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const isOutbound = email.direction === 'outbound';
  const dateStr = email.sent_at || email.received_at;
  // Ab sieben Tagen liefert formatVor selbst das Datum; dann nicht doppelt.
  const vor = formatVor(dateStr);
  const vorRelativ = vor !== formatDatum(dateStr);

  return (
    <div className="overflow-hidden rounded-lg border border-border">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        aria-expanded={isExpanded}
        className="w-full cursor-pointer p-4 text-left transition-colors hover:bg-accent"
      >
        <div className="flex items-start gap-3">
          <span
            className={cn(
              'flex size-8 shrink-0 items-center justify-center rounded-full',
              isOutbound ? 'bg-highlight-soft text-highlight' : 'bg-info-soft text-info',
            )}
          >
            {isOutbound ? <ArrowUpRight size={15} strokeWidth={1.75} aria-hidden /> : <ArrowDownLeft size={15} strokeWidth={1.75} aria-hidden />}
          </span>

          <div className="min-w-0 flex-1">
            <div className="mb-1 flex items-center gap-2">
              <span className="truncate font-medium">{email.subject || '(Kein Betreff)'}</span>
              {email.has_attachments && <Paperclip size={16} strokeWidth={1.75} className="shrink-0 text-muted-foreground" aria-label="Mit Anhang" />}
            </div>

            <div className="flex flex-wrap items-center gap-2 t-small">
              <StatusPill tone={isOutbound ? 'highlight' : 'info'} variant="plain">
                {isOutbound ? 'Gesendet' : 'Empfangen'}
              </StatusPill>
              <span className="text-muted-foreground">Von: {email.from_name || email.from_email}</span>
            </div>

            {dateStr && (
              <div className="mt-1 t-small text-muted-foreground">
                <span className="t-mono">{formatDatumZeit(dateStr)}</span>
                {vorRelativ && ` · ${vor}`}
              </div>
            )}
          </div>

          <ChevronDown
            size={18}
            strokeWidth={1.75}
            aria-hidden
            className={cn('shrink-0 text-muted-foreground transition-transform duration-150', isExpanded && 'rotate-180')}
          />
        </div>
      </button>

      {/* Ganzer Thread mit Anhaengen: /emails/:threadId (seit 05.09.2026 eingehaengt).
          Als eigener Link unter dem Aufklapp-Button, weil ein Link im Button
          kein gueltiges HTML waere. */}
      <div className="-mt-1 flex justify-end px-4 pb-3">
        <Link to={`/emails/${email.thread_id}`} className="inline-flex items-center gap-1 t-small text-link hover:underline touch:min-h-9">
          <ExternalLink size={14} strokeWidth={1.75} aria-hidden />
          {email.has_attachments ? 'Thread mit Anhängen öffnen' : 'Thread öffnen'}
        </Link>
      </div>

      {isExpanded && email.body_preview && (
        <div className="border-t border-border px-4 pb-4 pt-2 animate-ek-in">
          <div className="rounded-md bg-muted p-3">
            <pre className="whitespace-pre-wrap font-sans t-body">{email.body_preview}</pre>
          </div>
        </div>
      )}
    </div>
  );
}

export function EmailHistory({ customerId, contactId, initialLimit = 10 }: EmailHistoryProps) {
  const [offset, setOffset] = useState(0);
  const limit = initialLimit;

  const queryKey = customerId
    ? ['emails', 'customer-history', customerId, { limit, offset }]
    : contactId
    ? ['emails', 'contact-history', contactId, { limit, offset }]
    : null;

  const { data, isLoading, isError, error } = useQuery({
    queryKey: queryKey!,
    queryFn: () => {
      if (customerId) {
        return emailsApi.getCustomerHistory(customerId, { limit, offset });
      }
      if (contactId) {
        return emailsApi.getContactHistory(contactId, { limit, offset });
      }
      throw new Error('Either customerId or contactId must be provided');
    },
    enabled: !!(customerId || contactId),
  });

  if (!customerId && !contactId) {
    return null;
  }

  return (
    <Card
      title="E-Mails"
      icon={Mail}
      actions={data && data.total > 0 ? <CountBadge>{data.total}</CountBadge> : undefined}
    >
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <EmailSkeleton key={i} />
          ))}
        </div>
      ) : isError ? (
        <EmptyState icon={MailX} title="E-Mails ließen sich nicht laden" description={(error as Error)?.message || undefined} />
      ) : data?.entries.length === 0 ? (
        <EmptyState icon={Mail} title="Keine E-Mails" description="Synchronisierte E-Mails erscheinen hier." />
      ) : (
        <div className="space-y-3">
          {data?.entries.map((entry) => (
            <EmailCard key={entry.id} email={entry} />
          ))}

          {data?.has_more && (
            <div className="pt-4 text-center">
              <Button size="sm" onClick={() => setOffset(offset + limit)}>
                Weitere laden
              </Button>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

export default EmailHistory;
